import { Form, useFormikContext } from 'formik'
import { FaChevronRight } from 'react-icons/fa'
import { MdUpload } from 'react-icons/md'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Card } from 'src/components/atoms/Card'
import { Divider } from 'src/components/atoms/Divider'
import { OnClickLink } from 'src/components/atoms/Link'
import { Note } from 'src/components/atoms/Note'
import { Show } from 'src/components/atoms/Show'
import { FileType } from 'src/components/molecules/FileUpload'
import { FormField } from 'src/components/molecules/FormField'

import { FormFieldTypes } from 'src/types/form'

import { PlanterRegistrationValues } from './types'

export type PlanterRegistrationFormProps = {
  isAdmin?: boolean
  isSubmitting: boolean
  onSaveAsDraft: (values: PlanterRegistrationValues) => Promise<void>
  onCancel: () => void
}

export const PlanterRegistrationForm = ({
  isAdmin,
  isSubmitting,
  onSaveAsDraft,
  onCancel
}: PlanterRegistrationFormProps) => {
  const {
    dirty,
    values,
    isSubmitting: formikIsSubmitting
  } = useFormikContext<PlanterRegistrationValues>()

  const handleSaveAsDraft = async () => {
    await onSaveAsDraft(values)
  }
  return (
    <Card size='large'>
      <Form>
        <div className='flex flex-col mb-0!'>
          <Note type='infoNew' className='mb-8!'>
            Your experience planting and monitoring native trees helps us
            determine if we’re able to support your project. Include details of
            past projects, references, qualifications, and relevant training or
            work experience. This information can’t be changed once submitted.
          </Note>

          <FormField
            id='NumberOfTrees'
            label='1. Native tree planting history'
            description='Roughly how many native trees have you or your group planted and/or looked after in previous planting projects?'
            formFieldClassName='w-1/2 md:w-1/3'
            options={[
              { label: 'Less than 500', value: 'Less than 500' },
              { label: '500 to 5,000', value: '500 to 5,000' },
              { label: '5,000 to 10,000', value: '5,000 to 10,000' },
              { label: '10,000 to 20,000', value: '10,000 to 20,000' },
              { label: '20,000 or more', value: '20,000 or more' }
            ]}
            type={FormFieldTypes.select}
          />

          <FormField
            id='Description'
            label='2. Planting native trees'
            description="Describe your experience in organising and getting native trees into the ground. Be as descriptive as you can. Include any support you've had or people you've worked with."
            placeholder='Type here...'
            helpText='Tip: Write this up in another document, copy and paste it here.'
            resize
            rows={4}
            type={FormFieldTypes.textarea}
          />

          <FormField
            id='MonitoringDescription'
            label='3. Maintaining and monitoring native trees'
            description="Describe your experience maintaining and monitoring native trees. Be as descriptive as you can. Include any support you've had or people you've worked with."
            placeholder='Type here...'
            helpText='Tip: Write this up in another document, copy and paste it here.'
            resize
            rows={4}
            type={FormFieldTypes.textarea}
          />
          <FormField
            id='PlantingAssets'
            label='4. Photos (Optional)'
            description="Have photos of your previous planting projects? We'd love to see them."
            type={FormFieldTypes.file}
            fileType={FileType.image}
            renderDropzone={false}
            renderFilesAsList={false}
            uploadButton={
              <ButtonRebrand variant='forest'>
                <MdUpload className='mr-2' />
                <span>Upload photos</span>
              </ButtonRebrand>
            }
          />
          <FormField
            id='PlantingDocuments'
            label='5. Supporting documents (Optional)'
            description="Have supporting documents you want to add? We'd love to see them."
            helpText='E.g. Previous planting and site maintenance plans and previous monitoring results.'
            type={FormFieldTypes.file}
            fileType={FileType.document}
            renderDropzone={false}
            uploadButtonText='Add supporting documents if any'
            uploadButton={
              <ButtonRebrand variant='forest' className='mb-2'>
                <MdUpload className='mr-2' />
                <span>Add supporting documents if any</span>
              </ButtonRebrand>
            }
          />
          <Divider className='mt-0!' />
          <div className='flex flex-row justify-between mb-4'>
            <OnClickLink
              color='forestDeep'
              className='underline self-center'
              onClick={onCancel}
            >
              Cancel
            </OnClickLink>
            <div className='flex gap-2'>
              {/* Also don't show if application is already submitted */}
              <Show when={!isAdmin && values.WorkflowState !== 'Submitted'}>
                <ButtonRebrand
                  variant='outline'
                  disabled={isSubmitting || formikIsSubmitting || !dirty}
                  onClick={handleSaveAsDraft}
                >
                  Save as draft
                </ButtonRebrand>
              </Show>
              <ButtonRebrand
                variant='lime'
                disabled={
                  isSubmitting ||
                  formikIsSubmitting ||
                  values.WorkflowState === 'Submitted' ||
                  (!values.WorkflowState && !dirty)
                }
                type='submit'
                className='gap-2'
              >
                Submit
                <FaChevronRight size={12} />
              </ButtonRebrand>
            </div>
          </div>
        </div>
      </Form>
    </Card>
  )
}
